import { FC, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Route, Routes, useLocation, useParams } from "react-router-dom"
import CloseIcon from "@/assets/svgs/close.svg"
import {
  Backdrop,
  Box,
  Button,
  Grid,
  IconButton,
} from "@/components/mui.components"
import { useAppDispatch, useAppSelector } from "@/core/app/hooks"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"
import { fetchClinicalSettings } from "@/core/app/slices/user/userApis"
import { RootState } from "@/core/app/store"
import { useMyNavigation } from "@/hooks/useMyNavigation"
import PhotoList from "@/modules/Uassist/PhotoList"
import {
  getCaseById,
} from "@/core/app/slices/case/caseThunkApi"
import Form from "@/components/Form/Form"
import { FormContentRef } from "@/components/Form/types"
import { FormRenderMode, FormType } from "@/components/Form/types"
import FORM_CONSTANT from "@/core/app/slices/uassist/form.constant"
import { setIsRouteChangeBlocked } from "@/core/app/slices/navigationPopup/navigationPopupSlice"

const UassistRoutes: FC<{
  setUpperArchType: (val: number) => void
  setLowerArchType: (val: number) => void
}> = ({ setUpperArchType, setLowerArchType }) => {
  const { patientId, caseId } = useParams()
  const { caseRAtype } = useAppSelector(
    (state: RootState) => state.udTreatService,
  )
  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)
  const formRef = useRef<FormContentRef>(null)

  const location = useLocation()
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const navigate = useMyNavigation()
  const [isFormSummary, setIsFormSummary] = useState(false)
  const [formName, setFormName] = useState<string>("")
  const [isSavingAndContinueLater, setIsSavingAndContinueLater] = useState(false)
  const [isValidating, setIsValidating] = useState(false)
  
  const handleFormSaved = (val: boolean) => {
    if(isSavingAndContinueLater) {
      dispatch(setIsRouteChangeBlocked({isRouteChangeBlocked:false}))
      setTimeout(() => {
        navigate(`/patients`)
      }, 100);
    }
    else{
      setIsSavingAndContinueLater(false)
      setIsFormSummary(val)
    }
  }

  // Effect to handle form validation when saving to continue later
  useEffect(() => {
    const validateForm = async () => {
      if (isSavingAndContinueLater && !isValidating && formRef.current) {
        setIsValidating(true)
        try {
          formRef.current.setShowValidationErrors(true)
          if (formRef.current.showValidationErrors) {
            await formRef?.current?.validateErrors()
          }
          // Validation successful - handleFormSaved will be called by the Form component
        } catch (error) {
          console.error("Form validation failed:", error)
          setIsSavingAndContinueLater(false)
        } finally {
          setIsValidating(false)
        }
      }
    }
    
    validateForm()
  }, [isSavingAndContinueLater])

  useEffect(() => {
    setIsFormSummary(location.pathname.includes("/rxForm/summary"))
  }, [location?.pathname])

  useEffect(() => {
    if (caseRAtype) {
      setFormName(
        caseRAtype.toLowerCase() === "retainer"
          ? FORM_CONSTANT.RETAINER
          : FORM_CONSTANT.PRESCRIPTION,
      )
    } else if (caseDetail?.udesign_json) {
      const val = JSON.parse(caseDetail?.udesign_json)
      if (val.LowerType === "A" || val.UpperType === "A") {
        setFormName(FORM_CONSTANT.PRESCRIPTION)
      } else if (val.UpperType === "R" && val.LowerType === "R") {
        setFormName(FORM_CONSTANT.RETAINER)
      } else {
        navigate(`/patients/choosetype/patient/${patientId}/case/${caseId}`)
      }
    }
  }, [caseRAtype, caseDetail?.udesign_json])

  return (
    <Routes>
      <Route
        path="prescription"
        element={
          <SharedFormElement
            formRef={formRef}
            formName={formName}
            patientId={patientId}
            caseId={caseId}
            isFormSummary={isFormSummary}
            setIsFormSummary={setIsFormSummary}
            handleFormSaved={handleFormSaved}
            t={t}
            navigate={navigate}
            setUpperArchType={setUpperArchType}
            setLowerArchType={setLowerArchType}
            caseRAtype={caseRAtype}
            isSavingAndContinueLater={isSavingAndContinueLater}
            setIsSavingAndContinueLater={setIsSavingAndContinueLater}
          />
        }
      />
      <Route
        path="summary"
        element={
          <SharedFormElement
            formRef={formRef}
            formName={formName}
            patientId={patientId}
            caseId={caseId}
            isFormSummary={isFormSummary}
            setIsFormSummary={setIsFormSummary}
            handleFormSaved={handleFormSaved}
            t={t}
            navigate={navigate}
            setUpperArchType={setUpperArchType}
            setLowerArchType={setLowerArchType}
            caseRAtype={caseRAtype}
            setIsSavingAndContinueLater={setIsSavingAndContinueLater}
            isSavingAndContinueLater={isSavingAndContinueLater}
          />
        }
      />
    </Routes>
  )
}

const SharedFormElement = ({
  formRef,
  formName,
  patientId,
  caseId,
  isFormSummary,
  setIsFormSummary,
  handleFormSaved,
  t,
  navigate,
  setUpperArchType,
  setLowerArchType,
  caseRAtype,
  setIsSavingAndContinueLater,
  isSavingAndContinueLater
}) => {
  const [currentFormType, setCurrentFormType] = useState(null)

  return (
    <>
      <Box
        p={5}
      >
        {formName && (
          <Form
            ref={formRef}
            details={{
              patientId,
              caseId,
              formType: FormType.UASSIST,
              formName,
              uassistRound: 0,
              fetchSavedData: true,
              displayMeta: {
                formTitle: isFormSummary
                  ? caseRAtype.toLowerCase() === "retainer"
                    ? t("uassist.uDC.retainerSummary")
                    : t("uassist.uDC.prescriptionSummary")
                  : caseRAtype.toLowerCase() === "retainer"
                  ? t("uassist.uDC.retainer")
                  : t("uassist.uDC.prescription"),
                formDescription: isFormSummary ? t("uassist.summary.summarydesc") : null
              },
            }}
            mode={isFormSummary ? FormRenderMode.SUMMARY : FormRenderMode.EDIT}
            onChange={(data) => {
              isSavingAndContinueLater && setIsSavingAndContinueLater(false)
              if (
                setUpperArchType &&
                data.arches_to_treat?.upper &&
                data.arches_to_treat.upper.aligner_type
              ) {
                setUpperArchType(
                  data.arches_to_treat.upper.aligner_type === "Aligner" ? 0 : 1,
                )
              }
              if (
                setLowerArchType &&
                data.arches_to_treat?.lower &&
                data.arches_to_treat.lower.aligner_type
              ) {
                setLowerArchType(
                  data.arches_to_treat.lower.aligner_type === "Aligner" ? 0 : 1,
                )
              }
              console.log("Form data changed:", data)
            }}
            showValidationErrors={false}
            onFormSaved={handleFormSaved}
            setCurrentFormType={setCurrentFormType}
          />
        )}

        {currentFormType === FormType.UASSIST && (
          <Box
            pt={2}
            sx={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            {!isFormSummary ? (
              <Button
                variant="outlined"
                onClick={() => {
                  setIsSavingAndContinueLater(false)
                  if (formName === FORM_CONSTANT.PRESCRIPTION) {
                    navigate(`/records/patient/${patientId}/case/${caseId}/dentalchart`)
                  } else if (formName === FORM_CONSTANT.RETAINER) {
                    navigate(`/records/patient/${patientId}/case/${caseId}/scans`)
                  }
                }}
                sx={{
                  width: "96px",
                  height: "36px",
                  display: "inline-flex",
                }}
              >
                <ChevronLeftIcon
                  fontSize={"small"}
                  sx={{ marginRight: "3px" }}
                />
                {t("button.back")}
              </Button>
            ) : (
              <Button
                variant="outlined"
                onClick={() => {
                  setIsFormSummary(false)
                }}
                sx={{
                  width: "96px",
                  height: "36px",
                  display: "inline-flex",
                }}
              >
                <ChevronLeftIcon
                  fontSize={"small"}
                  sx={{ marginRight: "3px" }}
                />
                {t("button.edit")}
              </Button>
            )}
            <Box sx={{ flex: "1 1 auto" }} />
            {!isFormSummary ? (
              <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
                <Button
                  onClick={() => setIsSavingAndContinueLater(true)}
                  variant="outlined"
                  sx={{
                    width: "auto",
                    height: "36px",
                    display: "inline-flex",
                  }}
                >
                  {t("button.saveAndContinueLater")}
                </Button>
                <Button
                  onClick={async () => {
                    if(isSavingAndContinueLater) setIsSavingAndContinueLater(false)
                    try {
                      formRef.current?.setShowValidationErrors(true)
                      if (formRef.current.showValidationErrors) {
                        await formRef?.current?.validateErrors()
                      }
                      console.log("Form data updated successfully")
                    } catch (error) {
                      console.error("Error updating form data:", error)
                    }
                  }}
                  variant="contained"
                  sx={{
                    width: "92px",
                    height: "36px",
                    display: "inline-flex",
                  }}
                  disabled={false}
                >
                  {t("button.next")}
                  <ChevronRightIcon
                    sx={{ marginLeft: "3px" }}
                    fontSize={"small"}
                  />
                </Button>
              </Box>
            ) : (
              <Button
                onClick={async () => {
                  await formRef.current?.submitToUassist()
                }}
                variant="contained"
                sx={{
                  height: "36px",
                  display: "inline-flex",
                }}
              >
                {t("button.submittoUassist")}
                <ChevronRightIcon
                  sx={{ marginLeft: "3px" }}
                  fontSize={"small"}
                />
              </Button>
            )}
          </Box>
        )}
      </Box>
    </>
  )
}

const RxFormMoudle: FC = () => {
  const { t } = useTranslation()
  const { patientId, caseId } = useParams()
  const dispatch = useAppDispatch()
  const preview = useRef<HTMLInputElement>(null)
  const [selectedPreview, setSelectedPreview] = useState("")
  const [upperArchType, setUpperArchType] = useState<number>(null)
  const [lowerArchType, setLowerArchType] = useState<number>(null)

  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)
  const { rxPrescription } = useAppSelector(
    (state: RootState) => state.formService,
  )

  const { fileList } = useAppSelector((state: RootState) => state.PhotosService)

  const { routeFrom } = useAppSelector((state: RootState) => state.config)

  const { clinicalSettings, clinicalSettingsFetchStatus } = useAppSelector(
    (state: RootState) => state.userService,
  )

  useEffect(() => {
    dispatch(fetchClinicalSettings())
  }, [dispatch])

  // useEffect(() => {
  //   zipNames &&
  //     zipNames.length &&
  //     dispatch(
  //       fetchFileZips({
  //         patientId,
  //         caseId,
  //         zipNames,
  //       }),
  //     )
  // }, [zipNames])

  useEffect(() => {
    const handleScroll = () => {
      if (preview && preview.current) {
        const { top, height } = preview.current.getBoundingClientRect()
        //To determine if the columns have wrapped
        const parent = preview.current.parentElement?.offsetWidth
        const form = preview.current.nextSibling?.offsetWidth
        if (parent && form) {
          const isSticky =
            top <= 0 &&
            top + height > 0 &&
            parent >= form + preview.current.offsetWidth + 45
          preview.current.classList.toggle("preview-sticky", isSticky)
        }
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => {
      window.removeEventListener("scroll", handleScroll)
    }
  }, [preview])

  //Clean up clincal zipNames, zipList, and other data to avoid entering clincal with errors
  useEffect(() => {
    return () => {
      // dispatch(resetPrescriptionSchema())
      // dispatch(resetCase())
    }
  }, [])
  useEffect(() => {
    dispatch(getCaseById({ caseId, patientId }))
  }, [])
  return (
    <>
      <Grid
        container
        gap={"45px"}
        marginBottom={"48px"}
        paddingTop={2}
        justifyContent={"center"}
      >
        <Grid
          item
          display={"flex"}
          flexDirection={"column"}
          minWidth={"466px"}
          ref={preview}
          height={"720px"}
        >
          {/* <Grid sx={{ my: 2 }} display={"flex"}>
            <PatientBasicInfo
              patientData={patientData}
              caseDetail={caseDetail}
            />
          </Grid> */}
          <PhotoList
            setSelectedPreview={setSelectedPreview}
            upperArchType={upperArchType}
            lowerArchType={lowerArchType}
          />
        </Grid>
        <Grid
          item
          display={"flex"}
          flexDirection={"column"}
          gap={3}
          width={"640px"}
          style={{
            backgroundColor: "#fff",
            borderRadius: "16px"
          }}
        >
          <Box
            component={"div"}
            display={"flex"}
            justifyContent={"flex-end"}
            height={0}
            padding={"0"}
          >
            {/* <UButton
              sxProp={{ height: 30, width: 63 }}
              variant={"shade"}
              btnType={"button"}
              btnText={t("button.close")}
              size={"small"}
              onClickHandler={() => handleCloseButton()}
            /> */}
          </Box>
          <UassistRoutes
            setUpperArchType={setUpperArchType}
            setLowerArchType={setLowerArchType}
          />
        </Grid>
      </Grid>
      {selectedPreview && (
        <>
          <Backdrop
            sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open
          >
            <Box
              display="flex"
              justifyContent="center"
              position="fixed"
              top="120px"
              bottom="120px"
              left="120px"
              right="120px"
              overflow="hidden"
            >
              <Box
                id="backdrop"
                position="relative"
                width="inherit"
                height="inherit"
              >
                <img
                  src={selectedPreview}
                  alt={"fullscreenimg"}
                  style={{
                    width: "100%",
                    height: "100%",
                  }}
                  loading="lazy"
                />
                <Box
                  component={"div"}
                  id="closeicon"
                  position="absolute"
                  top="0px"
                  right="0px"
                  sx={{ padding: "8px" }}
                >
                  <IconButton
                    size="medium"
                    sx={{
                      fontSize: "large",
                      backgroundColor: "divider",
                      padding: "0px",
                      ":hover": { backgroundColor: "divider" },
                    }}
                    onClick={() => setSelectedPreview("")}
                  >
                    <Box
                      component={"img"}
                      src={CloseIcon}
                      alt={"Logo"}
                      loading="lazy"
                      sx={{ cursor: "pointer", padding: "12px" }}
                    />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Backdrop>
        </>
      )}
    </>
  )
}
export default RxFormMoudle
